import React from 'react';
import styled from 'styled-components';
import { FaColumns, FaFileAlt, FaKeyboard, FaShieldAlt } from 'react-icons/fa';
import { useLanguage } from '../i18n/LanguageContext';

const FeaturesSection = styled.section`
  padding: 4.5rem 1.5rem;
  background: rgba(255, 255, 255, 0.72);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const FeaturesContainer = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  max-width: 680px;
  margin-bottom: 2rem;
`;

const Eyebrow = styled.div`
  color: var(--color-accent);
  font-weight: 800;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0;
  margin-bottom: 0.55rem;
`;

const FeaturesTitle = styled.h2`
  font-size: clamp(2rem, 3.2vw, 3rem);
  line-height: 1.12;
  color: var(--color-text-primary);
  font-weight: 700;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  border-radius: var(--radius-large);
  overflow: hidden;
  box-shadow: var(--shadow-soft);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  padding: 1.4rem;
  border-right: 1px solid var(--color-border);
  min-height: 230px;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 1024px) {
    border-bottom: 1px solid var(--color-border);

    &:nth-child(2) {
      border-right: none;
    }

    &:nth-last-child(-n + 2) {
      border-bottom: none;
    }
  }

  @media (max-width: 640px) {
    border-right: none;

    &:nth-last-child(2) {
      border-bottom: 1px solid var(--color-border);
    }
  }

  svg {
    width: 28px;
    height: 28px;
    color: var(--color-accent);
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.08rem;
    color: var(--color-text-primary);
    margin-bottom: 0.55rem;
  }

  p {
    color: var(--color-text-secondary);
    line-height: 1.65;
    font-size: 0.96rem;
  }
`;

const Workflow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem;
  margin-top: 1.2rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const WorkflowPanel = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-large);
  background: var(--color-surface);
  padding: 1.2rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const Steps = styled.ol`
  display: grid;
  gap: 0.85rem;
  list-style: none;
  counter-reset: steps;
`;

const Step = styled.li`
  counter-increment: steps;
  display: grid;
  grid-template-columns: 30px 1fr;
  gap: 0.75rem;
  align-items: start;
  color: var(--color-text-secondary);
  line-height: 1.5;

  &::before {
    content: counter(steps);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: var(--radius-small);
    background: var(--color-accent-soft);
    color: var(--color-accent);
    font-weight: 800;
  }
`;

const Compatibility = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1rem;

  span {
    padding: 0.5rem 0.7rem;
    border-radius: var(--radius-small);
    border: 1px solid var(--color-border);
    background: #f8fafc;
    color: var(--color-text-secondary);
    font-weight: 700;
    font-size: 0.9rem;
  }
`;

const Features: React.FC = () => {
  const { t } = useLanguage();
  const icons = [FaFileAlt, FaColumns, FaKeyboard, FaShieldAlt];
  const featureItems = t('features.items', { returnObjects: true }) as Array<{ title: string; description: string }>;
  const steps = t('features.steps', { returnObjects: true }) as string[];

  return (
    <FeaturesSection id="features">
      <FeaturesContainer>
        <SectionHeader>
          <Eyebrow>{t('features.eyebrow')}</Eyebrow>
          <FeaturesTitle>{t('features.title')}</FeaturesTitle>
        </SectionHeader>

        <FeatureGrid>
          {featureItems.map((feature, index) => {
            const Icon = icons[index] || FaFileAlt;
            return (
              <FeatureItem key={feature.title}>
                <Icon />
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </FeatureItem>
            );
          })}
        </FeatureGrid>

        <Workflow>
          <WorkflowPanel>
            <Eyebrow>{t('features.workflowEyebrow')}</Eyebrow>
            <Steps>
              {steps.map((step) => (
                <Step key={step}>{step}</Step>
              ))}
            </Steps>
          </WorkflowPanel>
          <WorkflowPanel>
            <Eyebrow>{t('features.coverageEyebrow')}</Eyebrow>
            <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.65 }}>
              {t('features.coverageDescription')}
            </p>
            <Compatibility>
              <span>Markdown</span>
              <span>LaTeX</span>
              <span>JSON</span>
              <span>Links</span>
              <span>Images</span>
              <span>Dialog</span>
            </Compatibility>
          </WorkflowPanel>
        </Workflow>
      </FeaturesContainer>
    </FeaturesSection>
  );
};

export default Features;
